import { useState, useEffect, useMemo, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { ShoppingCart, CreditCard, User, BarChart3, ChevronDown, ChevronUp, Shield, Lock, CheckCircle2, Loader2, AlertTriangle, Sparkles } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Separator } from '@/components/ui/separator'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { useCart } from '@/lib/cart'
import { useAuth } from '@/lib/auth'
import { createCheckoutSession, CheckoutRequestError, priceChangeOf, isTierUnresolved, TIER_UNRESOLVED_MESSAGE } from '@/lib/stripe-checkout'
import { PriceChangeDialog } from '@/components/checkout/PriceChangeDialog'
import { supabase } from '@/lib/supabase'
import { DuffelCardForm } from '@duffel/components'
import { fetchComponentClientKey, bookFlightWithCard } from '@/lib/duffel-payment'
import { useDuffelCardFlow } from '@/hooks/useDuffelCardFlow'
import { AncillariesStep } from '@/components/checkout/AncillariesStep'
import { toast } from 'sonner'
import type { IdentityDocument, PassengerDetail, GuestDetail } from '@/types/cart'
import { 
  normalizeE164, 
  COUNTRY_PHONE_CONFIGS, 
  formatPhoneNumber, 
  validatePhoneNumber, 
  getPhoneConfigByDial,
  parsePhoneDigits
} from '@/lib/phone'
import {
  getCountriesWithPopularFirst,
  getAddressConfig,
  validatePostalCode,
  getStatesForCountry,
  countryHasStates,
  getStateLabel,
  formatPostalCode
} from '@/lib/address-validation'
import { TravelerForm } from '@/components/checkout/TravelerForm'
import { BookingDetailsCard } from '@/components/checkout/BookingDetailsCard'
import { Alert, AlertDescription } from '@/components/ui/alert'
// Paywall validation imports
import { canPurchase } from '@/lib/subscription-rules'
import { resolveRevalidationRules } from '@/lib/subscription-revalidation'
import type { CartValidationResult } from '@/types/cart'
// VOY-18: Flight Analysis imports
import type { FlightAnalysis, FlightSearchParamsForAnalysis } from '@/types/travel'
import { generateFlightAnalysis } from '@/lib/travel-api'
import { FlightAnalysisTab } from '@/components/travel/itinerary/FlightAnalysisTab'

// VOY-226: Use comprehensive country phone configs from phone.ts
// Sorted by popularity/usage for better UX
// Keys use ISO codes for uniqueness, but VALUES remain dial codes for API compatibility
const POPULAR_COUNTRY_CODES = ['US', 'CA', 'GB', 'MX', 'AR', 'BR', 'DE', 'FR', 'ES', 'IT', 'AU', 'JP', 'CN', 'IN', 'KR'];
const COUNTRY_CODES = [
  ...COUNTRY_PHONE_CONFIGS.filter(c => POPULAR_COUNTRY_CODES.includes(c.code)),
  ...COUNTRY_PHONE_CONFIGS.filter(c => !POPULAR_COUNTRY_CODES.includes(c.code))
]

/** Default age for child travelers when not specified (1–17; RateHawk requires age). */
const DEFAULT_CHILD_AGE = 8

// Loose shapes for the data embedded on cart items. The real domain types
// in @/types/travel are strict and evolve with search results, but cart
// items carry a normalised subset plus a handful of fields added during
// add-to-cart (apiPrice, duffelOfferId, selectedRoom, etc.). These local
// interfaces keep this file out of the `as any` pit without forcing
// downstream types to expose every optional field.
interface GuestSpec {
  adults?: number
  children?: number | number[]
}

interface FlightSliceSegment {
  departureTime?: string
  departing_at?: string
  [key: string]: unknown
}

interface FlightCartSlice {
  origin?: string
  destination?: string
  departureTime?: string
  segments?: FlightSliceSegment[]
}

interface FlightCartData {
  passengers?: { adults?: number; children?: number; infants?: number }
  slices?: FlightCartSlice[]
  totalPrice?: number
  price?: number
  origin?: string
  destination?: string
  offerId?: string
  duffelOfferId?: string
}

interface HotelSelectedRoom {
  id?: string
  rate_id?: string
  bookHash?: string
  book_hash?: string
  room_data_trans_id?: string
  apiPrice?: number
  totalPrice?: number
  guests?: GuestSpec | GuestSpec[]
  checkin?: string
  checkout?: string
}

interface HotelCartData {
  id?: string
  hotelId?: string
  hid?: string
  hotel_id?: string
  name?: string
  apiPrice?: number
  guests?: GuestSpec | GuestSpec[]
  selectedRoom?: HotelSelectedRoom
  checkin?: string
  checkIn?: string
  check_in?: string
  checkout?: string
  checkOut?: string
  check_out?: string
}

// The generated Supabase `Database` type at src/types/supabase.ts does not
// yet include the `orders` or `order_items` tables, so typed calls to
// supabase.from('orders')... fail to compile. Until the types are
// regenerated, reach through a single untyped alias instead of sprinkling
// `(supabase as any)` at every call site. Module-scoped so it's easy to
// find and remove in one place once the types catch up.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sb: any = supabase

export default function CheckoutPage() {
  const navigate = useNavigate()
  const { cart, clearCart, expiryState, openCart } = useCart()
  const { user } = useAuth()
  const [isProcessing, setIsProcessing] = useState(false)
  // VOY-551: the server re-priced the order to a total the user has not seen yet. Holds the request
  // to resend with acceptedTotal once they confirm.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [priceChange, setPriceChange] = useState<{ previous: number | null; current: number; request: any } | null>(null)

  // VOY-406: if the cart TTL elapses mid-session, kick the user out of checkout.
  // Also handles hard-reload mid-expiry: if the sessionStorage flag was set by
  // the cart provider before the page navigated, redirect immediately on mount
  // rather than waiting for the next countdown tick.
  useEffect(() => {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('voy-cart-expired') === '1') {
      sessionStorage.removeItem('voy-cart-expired')
      toast.error('Your cart session expired. Please start again.')
      navigate('/')
      return
    }
    if (expiryState === 'expired') {
      toast.error('Your cart session expired. Please start again.')
      navigate('/')
    }
  }, [expiryState, navigate])
  const [paymentMethod, setPaymentMethod] = useState('card')
  // VOY-396 Phase 2: Duffel direct card payments for flights-only carts.
  // Mixed carts (flight + hotel) keep using the existing Stripe session
  // path until a future ticket unifies both suppliers.
  const [duffelClientKey, setDuffelClientKey] = useState<string | null>(null)
  const [duffelClientKeyError, setDuffelClientKeyError] = useState<string | null>(null)
  // VOY-347: ancillaries step state. When the user has a flights-only
  // cart, we show an ancillaries step after the traveler form and
  // before payment. The selected services are threaded to /instant.
  const [selectedServices, setSelectedServices] = useState<Array<{ id: string; quantity: number }>>([])


  // Customer fields (billing)
  const [customerFirstName, setCustomerFirstName] = useState('')
  const [customerLastName, setCustomerLastName] = useState('')
  const [customerEmail, setCustomerEmail] = useState(user?.email || '')
  const [customerCountryCode, setCustomerCountryCode] = useState('+1') // Dial code for E.164
  const [customerPhone, setCustomerPhone] = useState('')
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    apartment: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'US'
  })

  // Passenger/Guest details
  const [passengers, setPassengers] = useState<PassengerDetail[]>([])
  const [hotelGuests, setHotelGuests] = useState<GuestDetail[]>([])
  const [saveInfo, setSaveInfo] = useState(true)
  const [agreeToTerms, setAgreeToTerms] = useState(false)
  
  // VOY-18: Flight Analysis state
  const [flightAnalysis, setFlightAnalysis] = useState<FlightAnalysis | null>(null)
  const [isLoadingAnalysis, setIsLoadingAnalysis] = useState(false)
  const [isAnalysisExpanded, setIsAnalysisExpanded] = useState(false) // Collapsed by default
  
  // VOY-226: Phone validation state
  const [phoneError, setPhoneError] = useState<string | null>(null)
  
  // Paywall validation state
  const [checkoutValidation, setCheckoutValidation] = useState<CartValidationResult | null>(null)

  // Clear "cart cleared after previous success" flag so loadCart won't empty cart while user is on checkout form
  useEffect(() => {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('voy-cart-cleared')
  }, [])

  // Read the true Duffel offer ID from a cart item. Flights are added to
  // the cart with `duffelOfferId` hoisted onto the CartItem (see cart.tsx)
  // or carried on the inner flight payload — fall through both shapes for
  // robustness. Returns null when the ID is missing so callers can decide
  // whether to gate the direct card path.
  const duffelOfferId = useMemo(() => {
    if (cart.items.length !== 1) return ''
    const item = cart.items[0]
    if (item.type !== 'flight') return ''
    const hoisted = (item as { duffelOfferId?: string }).duffelOfferId
    const inner = item.item as { offerId?: string; duffelOfferId?: string } | undefined
    return hoisted || inner?.offerId || inner?.duffelOfferId || ''
  }, [cart.items])

  // A cart is eligible for the Duffel direct card path when it contains
  // exactly one flight item (no hotels, no multi-flight splits) and the
  // cart item carries the real Duffel offer ID the 3DS session needs.
  // Feature-flagged off by default: Duffel rejected card payments access
  // for Voy's org (2026-04-16). The code stays in place behind this flag
  // in case Duffel reopens the product or a compatible provider (Kyte)
  // offers a similar tokenization flow.
  const duffelCardPaymentsEnabled = import.meta.env.VITE_ENABLE_DUFFEL_CARD_PAYMENTS === 'true'
  const canUseDuffelDirect = duffelCardPaymentsEnabled && duffelOfferId.length > 0

  // Mint the Duffel component client key once the eligible cart is known.
  // The key is short-lived (~48h) and bound to the cardholder session, so
  // a per-checkout fetch is acceptable. Cleanup cancels the in-flight
  // request on cart changes that invalidate the current payment intent.
  useEffect(() => {
    if (!canUseDuffelDirect) {
      setDuffelClientKey(null)
      setDuffelClientKeyError(null)
      return
    }
    let cancelled = false
    setDuffelClientKeyError(null)
    fetchComponentClientKey()
      .then((key) => {
        if (!cancelled) setDuffelClientKey(key)
      })
      .catch((err) => {
        if (cancelled) return
        const message = err instanceof Error ? err.message : 'Failed to initialize payment'
        setDuffelClientKeyError(message)
      })
    return () => {
      cancelled = true
    }
  }, [canUseDuffelDirect])

  const duffelCardFlow = useDuffelCardFlow({
    clientKey: duffelClientKey,
    offerId: duffelOfferId,
  })

  // Get current phone config for formatting
  const currentPhoneConfig = useMemo(() => {
    return getPhoneConfigByDial(customerCountryCode)
  }, [customerCountryCode])
  
  // Get current country ISO code from dial code (for Select value)
  const currentCountryCode = useMemo(() => {
    return currentPhoneConfig?.code || 'US'
  }, [currentPhoneConfig])
  
  // Handle phone input with formatting
  const handlePhoneChange = (value: string) => {
    const digits = parsePhoneDigits(value)
    if (currentPhoneConfig) {
      const formatted = formatPhoneNumber(digits, currentPhoneConfig.code)
      setCustomerPhone(formatted)
      // Validate as user types
      const validation = validatePhoneNumber(digits, currentPhoneConfig.code)
      setPhoneError(validation.isValid ? null : validation.error || null)
    } else {
      setCustomerPhone(digits)
      setPhoneError(null)
    }
  }
  
  // Reset phone when country changes. Intentionally fires only on
  // customerCountryCode — adding customerPhone or currentPhoneConfig would
  // re-run on every keystroke and loop through setCustomerPhone.
  useEffect(() => {
    if (customerPhone && currentPhoneConfig) {
      const digits = parsePhoneDigits(customerPhone)
      const formatted = formatPhoneNumber(digits, currentPhoneConfig.code)
      setCustomerPhone(formatted)
      const validation = validatePhoneNumber(digits, currentPhoneConfig.code)
      setPhoneError(validation.isValid ? null : validation.error || null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerCountryCode])
  
  // VOY-227: Address validation state
  const [postalCodeError, setPostalCodeError] = useState<string | null>(null)
  
  // Get current address config
  const currentAddressConfig = useMemo(() => {
    return getAddressConfig(billingAddress.country)
  }, [billingAddress.country])
  
  // Get states for current country
  const currentCountryStates = useMemo(() => {
    return getStatesForCountry(billingAddress.country)
  }, [billingAddress.country])
  
  // Get all countries with popular ones first
  const countriesList = useMemo(() => getCountriesWithPopularFirst(), [])
  
  // Handle postal code change with validation
  const handlePostalCodeChange = (value: string) => {
    const formatted = formatPostalCode(value, billingAddress.country)
    setBillingAddress({ ...billingAddress, postalCode: formatted })
    const validation = validatePostalCode(formatted, billingAddress.country)
    setPostalCodeError(validation.isValid ? null : validation.error || null)
  }
  
  // Reset state when country changes. Fires only on country — adding
  // postalCode would re-validate on every keystroke and duplicate what
  // handlePostalCodeChange already does.
  useEffect(() => {
    if (billingAddress.postalCode) {
      const validation = validatePostalCode(billingAddress.postalCode, billingAddress.country)
      setPostalCodeError(validation.isValid ? null : validation.error || null)
    }
    // Reset state if country changed and doesn't have states
    if (!countryHasStates(billingAddress.country)) {
      setBillingAddress(prev => ({ ...prev, state: '' }))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [billingAddress.country])

  // Initialize passengers and guests based on cart items. The inner
  // length checks prevent re-initialisation once the arrays exist, so
  // listing .length in deps is safe and satisfies exhaustive-deps.
  useEffect(() => {
    // Count required passengers from flights
    const flightItems = cart.items.filter(item => item.type === 'flight')
    let totalPassengers = 0

    flightItems.forEach(item => {
      const flightData = item.item as FlightCartData
      if (flightData.passengers) {
        totalPassengers += (flightData.passengers.adults || 1) +
                         (flightData.passengers.children || 0)
      } else {
        totalPassengers = Math.max(totalPassengers, 1)
      }
    })

    // Initialize passenger details if needed
    if (totalPassengers > 0 && passengers.length === 0) {
      const newPassengers: PassengerDetail[] = []
      for (let i = 0; i < totalPassengers; i++) {
        newPassengers.push({
          title: 'mr',
          given_name: i === 0 ? customerFirstName : '',
          family_name: i === 0 ? customerLastName : '',
          gender: 'm',
          born_on: '',
          email: i === 0 ? customerEmail : '',
          phone_number: '',
          phone_country_code: '+1',
          identity_documents: [
            {
              type: 'passport',
              issuing_country_code: 'US',
              unique_identifier: ''
            }
          ]
        })
      }
      setPassengers(newPassengers)
    }

    // Count required hotel guests (adults only; children are sent via item_data.guests[0].children)
    const hotelItems = cart.items.filter(item => item.type === 'hotel')
    let totalGuests = 0

    hotelItems.forEach(item => {
      const hotelData = item.item as HotelCartData
      // guests is [{ adults: N, children: number[] }]; use first room adults
      const adults = Array.isArray(hotelData.guests) && hotelData.guests.length > 0
        ? hotelData.guests[0].adults
        : (Array.isArray(hotelData.guests) ? undefined : hotelData.guests?.adults)
      totalGuests += (typeof adults === 'number' && adults >= 1 ? adults : 2)
    })

    // Initialize guest details if needed
    if (totalGuests > 0 && hotelGuests.length === 0) {
      const newGuests: GuestDetail[] = []
      for (let i = 0; i < totalGuests; i++) {
        newGuests.push({
          title: 'mr',
          first_name: i === 0 ? customerFirstName : '',
          last_name: i === 0 ? customerLastName : '',
          email: i === 0 ? customerEmail : undefined,
          is_lead_guest: i === 0
        })
      }
      setHotelGuests(newGuests)
    }
  }, [cart.items, customerFirstName, customerLastName, customerEmail, passengers.length, hotelGuests.length])

  // Sync first passenger / guest with customer info. Uses functional
  // setState so the latest arrays are read inside the updater — no need
  // to list passengers / hotelGuests in deps, and no stale-read risk.
  useEffect(() => {
    const phoneE164 = normalizeE164(customerPhone, customerCountryCode) || ''
    setPassengers(prev => {
      if (prev.length === 0) return prev
      const next = [...prev]
      next[0] = {
        ...next[0],
        given_name: customerFirstName,
        family_name: customerLastName,
        email: customerEmail,
        phone_number: phoneE164,
      }
      return next
    })
    setHotelGuests(prev => {
      if (prev.length === 0) return prev
      const next = [...prev]
      next[0] = {
        ...next[0],
        first_name: customerFirstName,
        last_name: customerLastName,
        email: customerEmail,
      }
      return next
    })
  }, [customerFirstName, customerLastName, customerEmail, customerCountryCode, customerPhone])
  
  // VOY-18: Extract search params from cart items for flight analysis
  const flightSearchParams = useMemo((): FlightSearchParamsForAnalysis | undefined => {
    const flightItem = cart.items.find(item => item.type === 'flight')
    if (!flightItem) return undefined
    
    const flightData = flightItem.item as FlightCartData
    const slices = flightData.slices || []
    
    if (slices.length === 0) return undefined
    
    const firstSlice = slices[0]
    const lastSlice = slices[slices.length - 1]
    const isRoundTrip = slices.length > 1
    
    // Extract origin and destination
    const origin = firstSlice?.origin || ''
    const destination = firstSlice?.destination || ''
    
    // Extract dates from multiple possible locations in the data structure
    // Try: segments[0].departureTime, slice.departureTime, itinerary dates
    const extractDate = (slice: FlightCartSlice | undefined): string => {
      // First try: segment departure time
      if (slice?.segments?.[0]?.departureTime) {
        return slice.segments[0].departureTime.split('T')[0]
      }
      // Second try: slice level departure time
      if (slice?.departureTime) {
        return slice.departureTime.split('T')[0]
      }
      // Third try: departing_at from Duffel format
      if (slice?.segments?.[0]?.departing_at) {
        return slice.segments[0].departing_at.split('T')[0]
      }
      return ''
    }
    
    const departureDate = extractDate(firstSlice)
    const returnDate = isRoundTrip ? extractDate(lastSlice) : undefined
    
    // Don't return params if we couldn't extract required dates
    if (!departureDate) {
      console.warn('[Checkout] Could not extract departure date from flight data:', firstSlice)
      return undefined
    }
    
    // Get passenger counts
    const passengerData = flightData.passengers || { adults: 1, children: 0 }
    
    return {
      origin,
      destination,
      departureDate,
      returnDate,
      passengers: {
        adults: passengerData.adults || 1,
        children: passengerData.children || 0,
      },
      tripType: isRoundTrip ? 'round-trip' : 'one-way',
    }
  }, [cart.items])

  // VOY-298: Child ages in checkout (editable; used when creating order)
  const [childAgesCheckout, setChildAgesCheckout] = useState<number[]>([])
  // Child names (editable; displayed in TravelerForm, same length as child count)
  const [childNamesCheckout, setChildNamesCheckout] = useState<{ first_name: string; last_name: string }[]>([])

  // VOY-298: Summary of hotel guests (adults + children) for TravelerForm label
  const hotelGuestSummary = useMemo(() => {
    const hotelItems = cart.items.filter(item => item.type === 'hotel')
    let adults = 0
    let children = 0
    hotelItems.forEach(item => {
      const hotelData = item.item as HotelCartData
      // Guests can be on item or selectedRoom; format: [{ adults, children: number[] }] or { adults, children }
      const guestsRaw = hotelData.guests ?? hotelData.selectedRoom?.guests
      if (Array.isArray(guestsRaw) && guestsRaw.length > 0) {
        const g = guestsRaw[0]
        adults += typeof g.adults === 'number' ? g.adults : 0
        const c = g.children
        children += Array.isArray(c) ? c.length : (typeof c === 'number' ? c : 0)
      } else if (guestsRaw && typeof guestsRaw === 'object' && !Array.isArray(guestsRaw)) {
        adults += typeof guestsRaw.adults === 'number' ? guestsRaw.adults : 0
        const c = guestsRaw.children
        children += Array.isArray(c) ? c.length : (typeof c === 'number' ? c : 0)
      }
    })
    if (adults === 0 && children === 0) return undefined
    return { adults: adults || 1, children }
  }, [cart.items])

  // VOY-298: Initialize child ages/names from cart; pad to hotelGuestSummary.children so TravelerForm always receives full-length arrays
  useEffect(() => {
    const hotelItems = cart.items.filter(item => item.type === 'hotel')
    const first = hotelItems[0]?.item as HotelCartData | undefined
    const guestsRaw = first?.guests ?? first?.selectedRoom?.guests
    let ages: number[] = []
    if (Array.isArray(guestsRaw) && guestsRaw[0]?.children) {
      const c = guestsRaw[0].children
      ages = Array.isArray(c) ? c.map((a: number) => Math.min(17, Math.max(1, Number(a) || DEFAULT_CHILD_AGE))) : []
    } else if (!Array.isArray(guestsRaw) && guestsRaw && typeof guestsRaw.children === 'number') {
      ages = [Math.min(17, Math.max(1, guestsRaw.children))]
    }
    const expectedCount = hotelGuestSummary?.children ?? ages.length
    if (expectedCount > 0) {
      const paddedAges = ages.length >= expectedCount ? ages : [...ages, ...Array(expectedCount - ages.length).fill(DEFAULT_CHILD_AGE)]
      setChildAgesCheckout(paddedAges.slice(0, expectedCount))
      setChildNamesCheckout(prev => {
        if (prev.length === expectedCount) return prev
        if (prev.length < expectedCount) return [...prev, ...Array.from({ length: expectedCount - prev.length }, () => ({ first_name: '', last_name: '' }))]
        return prev.slice(0, expectedCount)
      })
    } else {
      setChildAgesCheckout([])
      setChildNamesCheckout([])
    }
  }, [cart.items, hotelGuestSummary?.children])
  
  // VOY-18: Fetch flight analysis when we have search params and a flight in cart
  useEffect(() => {
    if (!flightSearchParams || flightAnalysis || isLoadingAnalysis) return
    
    const fetchAnalysis = async () => {
      try {
        setIsLoadingAnalysis(true)
        console.log('[Checkout] Fetching flight analysis for:', flightSearchParams)
        
        const response = await generateFlightAnalysis(flightSearchParams)
        
        if (response.type === 'structured' && response.data) {
          setFlightAnalysis(response.data)
          console.log('[Checkout] Flight analysis received:', response.data.recommendation.action)
        }
      } catch (error) {
        console.error('[Checkout] Error fetching flight analysis:', error)
      } finally {
        setIsLoadingAnalysis(false)
      }
    }
    
    fetchAnalysis()
  }, [flightSearchParams, flightAnalysis, isLoadingAnalysis])
  
  // Paywall: Validate checkout on mount and cart changes
  const validateCheckout = useCallback(async (): Promise<CartValidationResult> => {
    try {
      // Get subscription and derive rules
      const rules = await resolveRevalidationRules(user?.id ?? null)
      if (!rules) {
        // VOY-610: plan unknown (lookup failed) — never block as if Free. Fail open like the
        // catch below; cart-checkout re-quotes with the server-side tier.
        return { isValid: true }
      }
      
      // Check for special-priced items without subscription
      const specialPricedItems = cart.items.filter(item => item.priceType === 'special')
      if (specialPricedItems.length > 0 && !rules.canHaveSpecialPrice) {
        return {
          isValid: false,
          error: 'Your cart contains special-priced items but you do not have an active subscription.',
          upgradeRequired: true,
          suggestedTier: 'pro',
          blockedItems: specialPricedItems
        }
      }
      
      // Check ticket limit
      const totalItems = cart.items.reduce((sum, item) => sum + item.quantity, 0)
      if (rules.ticketLimit !== null && totalItems > rules.ticketLimit) {
        return {
          isValid: false,
          error: `Maximum ${rules.ticketLimit} items allowed per booking on your plan. You have ${totalItems} items.`,
          upgradeRequired: false
        }
      }
      
      // Check beneficiary policy (self-only tiers) - validate against passengers
      if (rules.beneficiaryPolicy === 'self-only' && rules.canHaveSpecialPrice && passengers.length > 0) {
        const userProfile = user ? {
          firstName: customerFirstName,
          lastName: customerLastName,
          email: customerEmail
        } : null
        
        const beneficiaries = passengers.map(p => ({
          firstName: p.given_name,
          lastName: p.family_name
        }))
        
        const purchaseResult = canPurchase(rules, passengers.length, beneficiaries, userProfile)
        if (!purchaseResult.isValid) {
          return {
            isValid: false,
            error: purchaseResult.error || 'Beneficiary validation failed',
            upgradeRequired: purchaseResult.upgradeRequired,
            suggestedTier: purchaseResult.suggestedTier
          }
        }
      }
      
      return { isValid: true }
    } catch (error) {
      console.error('[Checkout] Validation error:', error)
      // On error, allow checkout to proceed (fail open)
      return { isValid: true }
    }
  }, [cart.items, passengers, user, customerFirstName, customerLastName, customerEmail])
  
  // Run validation on mount and cart changes
  useEffect(() => {
    if (cart.items.length === 0) {
      setCheckoutValidation(null)
      return
    }
    
    validateCheckout().then(setCheckoutValidation)
  }, [cart.items, validateCheckout])

  const updatePassenger = (index: number, updates: Partial<PassengerDetail>) => {
    const updatedPassengers = [...passengers]
    
    // Handle phone number updates specially
    if ('phone_country_code' in updates || 'phone_number' in updates) {
      const countryCode = updates.phone_country_code || updatedPassengers[index].phone_country_code || '+1'
      const phoneNumber = updates.phone_number || updatedPassengers[index].phone_number || ''
      
      updatedPassengers[index] = {
        ...updatedPassengers[index],
        ...updates,
        phone_country_code: countryCode,
        phone_number: phoneNumber // Store raw number, format on submit
      }
    } else {
      updatedPassengers[index] = {
        ...updatedPassengers[index],
        ...updates
      }
    }
    
    setPassengers(updatedPassengers)
  }

  const updatePassengerDocument = (
    passengerIndex: number,
    docIndex: number,
    updates: Partial<IdentityDocument>,
  ) => {
    const updatedPassengers = [...passengers]
    if (!updatedPassengers[passengerIndex].identity_documents) {
      updatedPassengers[passengerIndex].identity_documents = []
    }
    const existingDoc = updatedPassengers[passengerIndex].identity_documents![docIndex] || {}
    const mergedDoc = {
      ...existingDoc,
      ...updates
    }
    if (!mergedDoc.type) {
      mergedDoc.type = 'passport'
    }
    if (!mergedDoc.issuing_country_code) {
      mergedDoc.issuing_country_code = 'US'
    }
    updatedPassengers[passengerIndex].identity_documents![docIndex] = mergedDoc
    setPassengers(updatedPassengers)
  }

  const updateGuest = (index: number, updates: Partial<GuestDetail>) => {
    const updatedGuests = [...hotelGuests]
    updatedGuests[index] = { ...updatedGuests[index], ...updates }
    setHotelGuests(updatedGuests)
  }

  // VOY-551: the user saw the new total and accepted it. Resend the same request with acceptedTotal;
  // the server charges only if it still equals its own quote.
  const confirmPriceChange = async () => {
    if (!priceChange) return
    const { request, current } = priceChange
    setPriceChange(null)
    setIsProcessing(true)
    try {
      const session = await createCheckoutSession({ ...request, acceptedTotal: current })
      if (session.url) window.location.href = session.url
    } catch (error) {
      setIsProcessing(false)
      const again = priceChangeOf(error)
      if (again) {
        setPriceChange({ ...again, request })
        return
      }
      // The cart TTL can run out while the dialog is open: same handling as the first submit.
      if (error instanceof CheckoutRequestError && (error.status === 410 || error.code === 'CART_EXPIRED')) {
        clearCart()
        toast.error('Your cart session expired. Please start again.')
        navigate('/')
        return
      }
      // VOY-554: membership tier unresolved (409 TIER_UNRESOLVED); nothing was charged.
      if (isTierUnresolved(error)) {
        toast.error(TIER_UNRESOLVED_MESSAGE)
        return
      }
      toast.error(error instanceof Error ? error.message : 'Failed to process checkout')
    }
  }

  const declinePriceChange = () => {
    setPriceChange(null)
    toast.info('Nothing was charged. Review your cart and search again for current prices.')
    openCart()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let lastCheckoutRequest: any = null
    e.preventDefault()
    
    if (!agreeToTerms) {
      toast.error('Please agree to the terms and conditions')
      return
    }
    
    // Paywall: Re-validate before checkout
    const validation = await validateCheckout()
    if (!validation.isValid) {
      toast.error(validation.error || 'Checkout validation failed')
      setCheckoutValidation(validation)
      return
    }

    setIsProcessing(true)

    try {
      // Compute main contact phone once and mirror to all passengers
      const mainPhoneE164 = normalizeE164(customerPhone, customerCountryCode)
      if (!mainPhoneE164) throw new Error('Invalid phone number')
      const formattedPassengers = passengers.map(p => ({
        ...p,
        phone_number: mainPhoneE164
      }))

      for (let i = 0; i < formattedPassengers.length; i++) {
        const doc = formattedPassengers[i].identity_documents?.[0]
        if (!doc?.unique_identifier || !doc?.issuing_country_code || !doc?.expires_on) {
          toast.error(`Passenger ${i + 1}: please complete passport number, issuing country, and expiry date.`)
          setIsProcessing(false)
          return
        }
      }

      // Guard: warn on US 555 numbers (Duffel rejects)
      if (mainPhoneE164.startsWith('+1555')) {
        toast.error('Please use a realistic US phone (not 555 prefix).');
        setIsProcessing(false);
        return;
      }

      // Create order in database
      const { data: orderData, error: orderError } = await sb
        .from('orders')
        .insert([
          {
            user_id: user?.id,
            customer_name: `${customerFirstName} ${customerLastName}`,
            customer_email: customerEmail,
            customer_phone: mainPhoneE164,
            billing_address: billingAddress,
            total_amount: cart.totalPrice,
            currency: 'USD',
          status: 'created',
          metadata: {
              cart_items: cart.items.length,
              flight_count: cart.flightCount,
              hotel_count: cart.hotelCount,
              payment_method: paymentMethod
            }
          },
        ])
        .select()
        .single()

      if (orderError) throw orderError

      const orderId = orderData?.id
      if (!orderId) {
        throw new Error('Order ID missing after creation')
      }

      const createdOrderItems: Record<string, { id?: string }> = {}

      // Persist flight items only; Duffel booking happens after Stripe authorization (webhook)
      const flightItems = cart.items.filter(item => item.type === 'flight')
      for (const flightItem of flightItems) {
        const flightData = flightItem.item as FlightCartData
        const passengerCounts = flightData.passengers || { adults: formattedPassengers.length, children: 0, infants: 0 }
        const expected = (passengerCounts?.adults || 0) + (passengerCounts?.children || 0) + (passengerCounts?.infants || 0)
        if (!passengerCounts || expected !== formattedPassengers.length) {
          toast.error(`This fare was priced for ${passengerCounts?.adults || 0} adults and ${passengerCounts?.children || 0} children. Update passenger ages or re-search.`)
          throw new Error('Passenger counts mismatch for selected fare')
        }
        
        // VOY-551: informational only. This is the DISPLAYED fare (it already includes the 4% fee),
        // not Duffel's price; cart-checkout charges from the Duffel offer via the backend quote.
        const apiPrice = flightData.totalPrice || flightData.price || flightItem.price
        const itemDataWithApiPrice = {
          ...flightItem.item,
          apiPrice,
        }
        
        const { data: inserted, error: insertError } = await sb
          .from('order_items')
          .insert([
            {
              order_id: orderId,
              item_type: 'flight',
              item_id: flightItem.id,
              item_data: itemDataWithApiPrice,
              quantity: 1,
              unit_price: flightItem.price,
              total_price: flightItem.price,
              currency: 'USD',
              passengers: formattedPassengers,
              status: 'pending'
            },
          ])
          .select()
          .single()
        if (insertError) {
          console.error('Failed to insert flight order_item:', insertError);
          toast.error(`Failed to save flight booking: ${insertError.message}`);
          throw insertError;
        }
        createdOrderItems[`flight-${flightItem.id}`] = inserted
      }

      // Process hotel bookings
      const hotelItems = cart.items.filter(item => item.type === 'hotel')
      for (const hotelItem of hotelItems) {
        const hotelData = hotelItem.item as HotelCartData
        
        // SECURITY: Store original API price for backend validation
        // For hotels, apiPrice comes from RateHawk (set by backend in selectedRoom)
        const apiPrice = hotelData.selectedRoom?.apiPrice || 
                         hotelData.apiPrice || 
                         hotelData.selectedRoom?.totalPrice ||
                         hotelItem.price
        
        // Extract RateHawk identifiers for backend orchestration
        // Backend expects: ratehawkHotelId/ratehawk_hotel_id and ratehawkRateId/ratehawk_rate_id or bookHash/book_hash
        // VOY-268: Prioritize hotelId (original ID) over id (unique cart ID)
        const hotelId = hotelData.hotelId || hotelData.id || hotelData.hid || hotelData.hotel_id
        const rateId = hotelData.selectedRoom?.id || 
                       hotelData.selectedRoom?.rate_id || 
                       hotelData.selectedRoom?.bookHash || 
                       hotelData.selectedRoom?.book_hash
        const roomDataTransId = hotelData.selectedRoom?.room_data_trans_id
        
        // VOY-268 Part 2: Extract search parameters for book_hash refresh if prebook fails
        // Try multiple sources: hotelData directly, hotelItem.item (the full hotel object), or selectedRoom
        const checkin = hotelData.checkin 
          || hotelData.checkIn 
          || hotelData.check_in
          || (hotelItem.item as HotelCartData)?.checkin
          || (hotelItem.item as HotelCartData)?.checkIn
          || (hotelItem.item as HotelCartData)?.check_in
          || (hotelItem.item as HotelCartData)?.selectedRoom?.checkin;
        const checkout = hotelData.checkout 
          || hotelData.checkOut 
          || hotelData.check_out
          || (hotelItem.item as HotelCartData)?.checkout
          || (hotelItem.item as HotelCartData)?.checkOut
          || (hotelItem.item as HotelCartData)?.check_out
          || (hotelItem.item as HotelCartData)?.selectedRoom?.checkout;
        const guestsFromCart = hotelData.guests 
          || (hotelItem.item as HotelCartData)?.guests
          || (hotelItem.item as HotelCartData)?.selectedRoom?.guests
          || [{ adults: 1, children: [] }];
        // VOY-298: Use ages from checkout form (childAgesCheckout); ensure we always send child data when this room has children
        const firstRoom = Array.isArray(guestsFromCart) ? guestsFromCart[0] : guestsFromCart
        const adultsCount = firstRoom?.adults ?? 1
        const fromRoom = Array.isArray(firstRoom?.children) ? firstRoom.children.length : 0
        // When single hotel, also trust hotelGuestSummary so we don't lose children if cart structure was missing them
        const expectedChildren = hotelItems.length === 1 && (hotelGuestSummary?.children ?? 0) > fromRoom
          ? hotelGuestSummary!.children
          : fromRoom
        // Validate child names before submitting (RateHawk rejects placeholder "Child")
        if (expectedChildren > 0) {
          const missing = childNamesCheckout.slice(0, expectedChildren).findIndex(n => !(n?.first_name || '').trim() || !(n?.last_name || '').trim())
          if (missing !== -1) {
            toast.error('Please enter first and last name for all children.')
            setIsProcessing(false)
            return
          }
        }
        let childrenAges: number[] = childAgesCheckout.length > 0
          ? childAgesCheckout
          : (Array.isArray(firstRoom?.children) ? firstRoom.children.map((a: number) => Math.min(17, Math.max(1, Number(a) || DEFAULT_CHILD_AGE))) : [])
        // If this room has children but ages array is short (e.g. cart from before child ages), pad with default age
        if (expectedChildren > 0 && childrenAges.length < expectedChildren) {
          childrenAges = [...childrenAges, ...Array(expectedChildren - childrenAges.length).fill(DEFAULT_CHILD_AGE)].slice(0, expectedChildren)
        } else if (expectedChildren > 0) {
          childrenAges = childrenAges.slice(0, expectedChildren).map((a) => Math.min(17, Math.max(1, Number(a) || DEFAULT_CHILD_AGE)))
        }
        const guests = [{ adults: adultsCount, children: childrenAges }];
        // Full guest list for RateHawk: adults + children with names (so backend sends e.g. Catalina instead of Child)
        const guestsForOrderItem: Array<{ first_name: string; last_name: string; is_child?: boolean; age?: number }> = [
          ...hotelGuests.map(g => ({ first_name: g.first_name, last_name: g.last_name })),
          ...childNamesCheckout.slice(0, expectedChildren).map((name, i) => ({
            first_name: (name.first_name || '').trim() || 'Child',
            last_name: (name.last_name || '').trim() || 'Guest',
            is_child: true,
            age: childrenAges[i]
          }))
        ];
        
        // Debug logging for VOY-268
        if (!checkin || !checkout) {
          console.warn('[Checkout][VOY-268] Missing checkin/checkout in hotelData:', {
            hasCheckin: !!checkin,
            hasCheckout: !!checkout,
            hotelDataKeys: Object.keys(hotelData),
            hotelItemKeys: hotelItem.item ? Object.keys(hotelItem.item as HotelCartData) : []
          });
        }
        
        const itemDataWithApiPrice = {
          ...hotelItem.item,
          apiPrice, // Original price from RateHawk API
          // VOY-268: Store original hotel ID explicitly (not the unique cart ID)
          hotelId: hotelId,
          // RateHawk identifiers for backend orchestration (supports both camelCase and snake_case)
          ratehawkHotelId: hotelId,
          ratehawk_hotel_id: hotelId,
          ratehawkRateId: rateId,
          ratehawk_rate_id: rateId,
          bookHash: rateId,
          book_hash: rateId,
          // VOY-268 Part 2: Store search parameters for book_hash refresh (always store if available)
          ...(checkin && { checkin, checkIn: checkin, check_in: checkin }),
          ...(checkout && { checkout, checkOut: checkout, check_out: checkout }),
          ...(guests && { guests }),
          ...(roomDataTransId && {
            roomDataTransId: roomDataTransId,
            room_data_trans_id: roomDataTransId
          })
        }
        
        const { data: inserted, error: hotelInsertError } = await sb
          .from('order_items')
          .insert([
            {
              order_id: orderId,
              item_type: 'hotel',
              item_id: hotelItem.id,
              item_data: itemDataWithApiPrice,
              quantity: 1,
              unit_price: hotelItem.price,
              total_price: hotelItem.price,
              currency: 'USD',
              guests: guestsForOrderItem.length > 0 ? guestsForOrderItem : hotelGuests,
              status: 'pending'
            },
          ])
        if (hotelInsertError) {
          console.error('Failed to insert hotel order_item:', hotelInsertError);
          toast.error(`Failed to save hotel booking: ${hotelInsertError.message}`);
          throw hotelInsertError;
        }
        createdOrderItems[`hotel-${hotelItem.id}`] = inserted
      }

      // VOY-396 Phase 2: Duffel direct card payment for flights-only carts.
      // Tokenise + 3DS via @duffel/components, then hit the backend
      // instant endpoint which creates the order with payments: [{
      // type: 'card', three_d_secure_session_id }] in a single atomic
      // call. Mixed carts (flight + hotel) fall through to the Stripe
      // session path below unchanged.
      //
      // TODO(VOY-396 Phase 3): if authorizePayment() or bookFlightWithCard()
      // throws, the Supabase `order` and `order_items` rows above have
      // already committed. The user can retry and will create a second
      // orphaned `order` row on each failed attempt. Phase 3 adds a
      // `payment_status` column on order_items and a cleanup job.
      if (canUseDuffelDirect) {
        try {
          const threeDSecureSessionId = await duffelCardFlow.authorizePayment()
          const flightItem = cart.items[0]
          const flightOrderItem = createdOrderItems[`flight-${flightItem.id}`]
          const flightData = flightItem.item as {
            passengers?: { adults?: number; children?: number; infants?: number }
          }
          await bookFlightWithCard({
            offerId: duffelOfferId,
            passengers: formattedPassengers,
            contact: { email: customerEmail, phone: mainPhoneE164 },
            metadata: {
              order_item_id: flightOrderItem?.id,
              order_id: orderId,
            },
            threeDSecureSessionId,
            passengerCounts: flightData?.passengers,
            // VOY-347: include any ancillary services the user selected
            services: selectedServices.length > 0 ? selectedServices : undefined,
          })
          navigate(`/checkout/success?order_id=${orderId}`)
          return
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Payment failed. Please try again.'
          console.error('[checkout] Duffel card booking failed:', message)
          toast.error(message)
          setIsProcessing(false)
          return
        }
      }

      // Handle payment - create Stripe session.
      // The CheckoutSessionRequest type in @/lib/stripe-checkout expects a
      // different shape than what we pass here (pre-existing mismatch from
      // before this file was typed). Reconciling both sides is a dedicated
      // refactor, not VOY-396 scope.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const checkoutRequest: any = {
        orderId: orderId,
        customerEmail: customerEmail,
        items: cart.items.map(item => {
          const name = item.type === 'flight'
            ? (() => {
                const f = item.item as FlightCartData
                return `Flight: ${f.origin ?? 'Origin'} to ${f.destination ?? 'Destination'}`
              })()
            : `Hotel: ${(item.item as HotelCartData).name ?? 'Unknown'}`
          return {
            name,
            price: item.price, // dollars; the function multiplies by 100
            quantity: 1,
          }
        }),
        successUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}&order_id=${orderId}`,
        cancelUrl: `${window.location.origin}/checkout/cancel?order_id=${orderId}`,
        metadata: {
          order_id: orderId,
          user_id: user?.id || '',
          flight_count: cart.flightCount.toString(),
          hotel_count: cart.hotelCount.toString()
        }
      }
      lastCheckoutRequest = checkoutRequest
      const session = await createCheckoutSession(checkoutRequest)

      if (session.url) {
        window.location.href = session.url
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to process checkout'
      console.error('Checkout error:', error)
      // VOY-406: backend gate — cart exceeded CHECKOUT_TTL_SECONDS.
      if (error instanceof CheckoutRequestError && (error.status === 410 || error.code === 'CART_EXPIRED')) {
        clearCart()
        toast.error('Your cart session expired. Please start again.')
        setIsProcessing(false)
        navigate('/')
        return
      }
      // VOY-551: the total changed since the user reviewed it. Show both and ask; nothing is charged.
      const change = priceChangeOf(error)
      if (change && lastCheckoutRequest) {
        setPriceChange({ ...change, request: lastCheckoutRequest })
        setIsProcessing(false)
        return
      }
      // VOY-554: the membership tier could not be resolved (a 409, but not a price change).
      if (isTierUnresolved(error)) {
        toast.error(TIER_UNRESOLVED_MESSAGE)
        setIsProcessing(false)
        return
      }
      // VOY-551: the server re-reads supplier prices before charging.
      if (error instanceof CheckoutRequestError && (error.status === 409 || error.code === 'CHANGED_PRICE')) {
        toast.error('A price in your cart changed or is no longer available. Remove that item and search again to see the current price.')
        setIsProcessing(false)
        return
      }
      if (error instanceof CheckoutRequestError && error.code === 'PRICE_UNVERIFIED') {
        toast.error("We couldn't confirm the current price. Nothing was charged; please try again in a moment.")
        setIsProcessing(false)
        return
      }
      toast.error(message)
      setIsProcessing(false)
    }
  }

  if (cart.items.length === 0) {
    return (
      <div className="container mx-auto py-12">
        <Card>
          <CardContent className="text-center py-12">
            <ShoppingCart className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Your cart is empty</h2>
            <p className="text-muted-foreground mb-4">Add some flights or hotels to get started</p>
            <Button onClick={() => navigate('/')}>
              Start Shopping
            </Button>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/20">
      <PriceChangeDialog change={priceChange} onConfirm={confirmPriceChange} onDecline={declinePriceChange} />
      <div className="container mx-auto px-4 py-6 lg:py-10">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">Secure Checkout</h1>
          <p className="text-muted-foreground mt-1">Complete your booking in just a few steps</p>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap items-center gap-4 mt-4">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Shield className="h-4 w-4 text-emerald-500" />
              <span>Secure Payment</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Lock className="h-4 w-4 text-emerald-500" />
              <span>SSL Encrypted</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <span>Instant Confirmation</span>
            </div>
          </div>
        </div>
        
      {/* Paywall: Validation Alert */}
        {checkoutValidation && !checkoutValidation.isValid && (
          <Alert variant="destructive" className="mb-6">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription className="flex items-center justify-between">
              <span>{checkoutValidation.error}</span>
              {checkoutValidation.upgradeRequired && (
                <Button variant="outline" size="sm" asChild className="ml-4">
                  <Link to="/subscription">
                    <Sparkles className="h-4 w-4 mr-1" />
                    Upgrade Plan
                  </Link>
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}
        
      <form onSubmit={handleSubmit}>
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <User className="h-4 w-4 text-primary" />
                    </div>
                  Customer Information
                </CardTitle>
            </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="firstName">First Name *</Label>
                      <Input
                        id="firstName"
                      value={customerFirstName}
                      onChange={(e) => setCustomerFirstName(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="lastName">Last Name *</Label>
                      <Input
                        id="lastName"
                      value={customerLastName}
                      onChange={(e) => setCustomerLastName(e.target.value)}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <div className="flex gap-2">
                    <Select 
                      key="customer-phone-select"
                      value={`customer-phone-${currentCountryCode}`}
                      onValueChange={(value) => {
                        // Extract ISO code (remove customer-phone- prefix)
                        const isoCode = value.replace('customer-phone-', '')
                        // Find country config and get dial code
                        const countryConfig = COUNTRY_PHONE_CONFIGS.find(c => c.code === isoCode)
                        if (countryConfig) {
                          setCustomerCountryCode(countryConfig.dial)
                        }
                      }}
                    >
                      <SelectTrigger className="w-[140px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {COUNTRY_CODES.map((c) => (
                          <SelectItem key={`customer-phone-${c.code}`} value={`customer-phone-${c.code}`}>
                            {c.flag} {c.dial}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex-1 space-y-1">
                    <Input
                      id="phone"
                      type="tel"
                        placeholder={currentPhoneConfig?.placeholder || '(555) 123-4567'}
                      value={customerPhone}
                        onChange={(e) => handlePhoneChange(e.target.value)}
                        className={phoneError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                      required
                    />
                      {phoneError && (
                        <p className="text-xs text-red-500">{phoneError}</p>
                      )}
                  </div>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {currentPhoneConfig ? `Format: ${currentPhoneConfig.format}` : 'Enter your phone number'}
                  </p>
                </div>

                <Separator />

                <div className="space-y-4">
                  <h3 className="font-medium">Billing Address</h3>
                  
                  <div className="space-y-2">
                    <Label htmlFor="street">Street Address *</Label>
                    <Input
                      id="street"
                      value={billingAddress.street}
                      onChange={(e) => setBillingAddress({...billingAddress, street: e.target.value})}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="apartment">Apartment, suite, etc.</Label>
                    <Input
                      id="apartment"
                      value={billingAddress.apartment}
                      onChange={(e) => setBillingAddress({...billingAddress, apartment: e.target.value})}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="city">City *</Label>
                      <Input
                        id="city"
                        value={billingAddress.city}
                        onChange={(e) => setBillingAddress({...billingAddress, city: e.target.value})}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="state">{currentAddressConfig ? getStateLabel(billingAddress.country) : 'State/Province'} {currentAddressConfig?.hasStates ? '*' : ''}</Label>
                      {currentCountryStates ? (
                        <Select 
                          value={billingAddress.state} 
                          onValueChange={(value) => setBillingAddress({...billingAddress, state: value})}
                        >
                          <SelectTrigger id="state">
                            <SelectValue placeholder={`Select ${getStateLabel(billingAddress.country)}`} />
                          </SelectTrigger>
                          <SelectContent className="max-h-[300px]">
                            {currentCountryStates.map(state => (
                              <SelectItem key={state.code} value={state.code}>
                                {state.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                      <Input
                        id="state"
                        value={billingAddress.state}
                        onChange={(e) => setBillingAddress({...billingAddress, state: e.target.value})}
                          placeholder={currentAddressConfig?.stateLabel || 'State/Province'}
                      />
                      )}
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postalCode">
                        Postal Code {currentAddressConfig?.postalCodeRequired ? '*' : ''}
                      </Label>
                      <div className="space-y-1">
                      <Input
                        id="postalCode"
                        value={billingAddress.postalCode}
                          onChange={(e) => handlePostalCodeChange(e.target.value)}
                          placeholder={currentAddressConfig?.postalCodePlaceholder || '12345'}
                          className={postalCodeError ? 'border-red-500 focus-visible:ring-red-500' : ''}
                          required={currentAddressConfig?.postalCodeRequired}
                        />
                        {postalCodeError && (
                          <p className="text-xs text-red-500">{postalCodeError}</p>
                        )}
                        {currentAddressConfig && !postalCodeError && (
                          <p className="text-xs text-muted-foreground">
                            Format: {currentAddressConfig.postalCodeFormat}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="country">Country *</Label>
                      <Select value={billingAddress.country} onValueChange={(value) => setBillingAddress({...billingAddress, country: value})}>
                        <SelectTrigger id="country">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {countriesList.map(country => (
                                      <SelectItem key={country.code} value={country.code}>
                              {country.flag} {country.name}
                                      </SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                  </div>
                </CardContent>
              </Card>

            {/* VOY-228: Consolidated Traveler Form */}
            {(passengers.length > 0 || hotelGuests.length > 0) && (
              <TravelerForm
                hasFlights={cart.flightCount > 0}
                hasHotels={cart.hotelCount > 0}
                passengers={passengers}
                onUpdatePassenger={updatePassenger}
                onUpdatePassengerDocument={updatePassengerDocument}
                guests={hotelGuests}
                onUpdateGuest={updateGuest}
                hotelGuestSummary={hotelGuestSummary}
                childAges={childAgesCheckout}
                onChildAgesChange={setChildAgesCheckout}
                childNames={childNamesCheckout}
                onChildNamesChange={setChildNamesCheckout}
                primaryFirstName={customerFirstName}
                primaryLastName={customerLastName}
                primaryEmail={customerEmail}
              />
            )}

            {/* VOY-347: Ancillaries Step — bags, seats, CFAR.
                Gated on canUseDuffelDirect because the Stripe path does
                not thread selectedServices to the booking — the user would
                select extras that silently never reach Duffel. When the
                Stripe path or card-passthrough supports services, this
                gate can relax to just duffelOfferId. */}
            {canUseDuffelDirect && (
              <Card className="shadow-sm hover:shadow-md transition-shadow">
                <CardHeader className="pb-4">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                      <ShoppingCart className="h-4 w-4 text-primary" />
                    </div>
                    Flight Extras
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <AncillariesStep
                    offerId={duffelOfferId}
                    onComplete={(services) => {
                      setSelectedServices(services)
                    }}
                    onSkip={() => {
                      setSelectedServices([])
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Payment Method */}
            <Card className="shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <CreditCard className="h-4 w-4 text-primary" />
                  </div>
                  Payment Method
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <RadioGroup value={paymentMethod} onValueChange={setPaymentMethod}>
                  <div className="flex items-center space-x-3 p-3 rounded-lg border border-primary bg-primary/5">
                    <RadioGroupItem value="card" id="card" />
                    <Label htmlFor="card" className="flex items-center gap-2 cursor-pointer flex-1">
                      <CreditCard className="h-4 w-4" />
                      Credit/Debit Card
                    </Label>
                    <div className="flex items-center gap-1">
                      <img src="https://cdn.jsdelivr.net/gh/lipis/flag-icons/flags/4x3/us.svg" alt="Visa" className="h-4 w-6 rounded" />
                    </div>
                  </div>
                </RadioGroup>

                {paymentMethod === 'card' && canUseDuffelDirect && (
                  <div className="p-4 rounded-lg border bg-card space-y-4">
                    <div className="flex items-start gap-3">
                      <Lock className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium">Secure card payment</p>
                        <p className="text-xs text-muted-foreground mt-1">
                          Card details are collected directly by Duffel in a PCI-compliant iframe and never touch our servers. If your bank requires 3D Secure, a verification window will appear before the charge is confirmed.
                        </p>
                      </div>
                    </div>
                    {duffelClientKeyError ? (
                      <div className="flex items-start gap-2 p-3 rounded-md border border-destructive/50 bg-destructive/10">
                        <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="font-medium text-destructive text-sm">Payment initialization failed</p>
                          <p className="text-xs text-muted-foreground mt-1">{duffelClientKeyError}</p>
                        </div>
                      </div>
                    ) : !duffelClientKey ? (
                      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span>Loading secure payment form…</span>
                      </div>
                    ) : (
                      <DuffelCardForm
                        ref={duffelCardFlow.cardFormRef}
                        {...duffelCardFlow.cardFormProps}
                      />
                    )}
                  </div>
                )}

                {paymentMethod === 'card' && !canUseDuffelDirect && (
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10 rounded-lg border border-emerald-200/50 dark:border-emerald-800/30">
                    <div className="flex items-start gap-3">
                      <Lock className="h-5 w-5 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                      <div>
                        <p className="text-sm font-medium text-emerald-800 dark:text-emerald-300">Secure Payment via Stripe</p>
                        <p className="text-xs text-emerald-700/80 dark:text-emerald-400/80 mt-1">
                          You will be redirected to Stripe's secure checkout page to complete your payment. Your card details are never stored on our servers.
                        </p>
                      </div>
                    </div>
                    </div>
                  )}
            </CardContent>
          </Card>
          </div>

          {/* Order Summary Sidebar */}
          <div className="lg:col-span-1 space-y-4">
            {/* VOY-18: Flight Analysis Card - Collapsible */}
            {(flightSearchParams || flightAnalysis || isLoadingAnalysis) && (
              <Card className="border-blue-200/50 dark:border-blue-800/50">
                <Collapsible open={isAnalysisExpanded} onOpenChange={setIsAnalysisExpanded}>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-gray-50 dark:hover:bg-zinc-800/50 transition-colors">
                      <CardTitle className="flex items-center justify-between text-base">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="h-4 w-4 text-blue-500" />
                          Flight Analysis
                        </div>
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0">
                          {isAnalysisExpanded ? (
                            <ChevronUp className="h-4 w-4" />
                          ) : (
                            <ChevronDown className="h-4 w-4" />
                          )}
                        </Button>
                      </CardTitle>
                      {!isAnalysisExpanded && flightAnalysis && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Recommendation: <span className={
                            flightAnalysis.recommendation.action === 'BUY_NOW' ? 'text-emerald-600 font-medium' :
                            flightAnalysis.recommendation.action === 'MONITOR' ? 'text-amber-600 font-medium' :
                            'text-rose-600 font-medium'
                          }>
                            {flightAnalysis.recommendation.action === 'BUY_NOW' ? 'Book Now' :
                             flightAnalysis.recommendation.action === 'MONITOR' ? 'Monitor Prices' :
                             'Wait to Book'}
                          </span>
                        </p>
                      )}
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="max-h-[400px] overflow-y-auto">
                        <FlightAnalysisTab
                          analysis={flightAnalysis}
                          searchParams={flightSearchParams}
                          isLoading={isLoadingAnalysis}
                        />
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            )}
            
            <Card className="sticky top-4 shadow-lg border-2">
              <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-t-lg">
                <CardTitle className="text-lg">Order Summary</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4">
                {/* VOY-229: Detailed booking cards */}
                <BookingDetailsCard items={cart.items} />

              <Separator />

                {/* Price breakdown */}
                <div className="space-y-3 p-4 bg-muted/30 rounded-lg">
                <div className="flex justify-between text-sm">
                    <p className="text-muted-foreground">Subtotal</p>
                    <p className="font-medium">${cart.totalPrice.toFixed(2)}</p>
                </div>
                <div className="flex justify-between text-sm">
                    <p className="text-muted-foreground">Taxes & Fees</p>
                    <p className="text-muted-foreground">Included</p>
                </div>
                <Separator />
                  <div className="flex justify-between items-center">
                    <p className="font-semibold">Total</p>
                    <p className="text-2xl font-bold text-primary">${cart.totalPrice.toFixed(2)}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <Checkbox 
                      id="save-info" 
                      checked={saveInfo}
                      onCheckedChange={(checked) => setSaveInfo(checked as boolean)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="save-info" className="text-sm cursor-pointer leading-relaxed">
                      Save my information for faster checkout next time
                    </Label>
              </div>

                  <div className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <Checkbox 
                      id="terms" 
                      checked={agreeToTerms}
                      onCheckedChange={(checked) => setAgreeToTerms(checked as boolean)}
                      className="mt-0.5"
                    />
                    <Label htmlFor="terms" className="text-sm cursor-pointer leading-relaxed">
                      I agree to the <a href="/terms" className="text-primary hover:underline">terms and conditions</a> and <a href="/privacy" className="text-primary hover:underline">privacy policy</a>
                    </Label>
                  </div>
              </div>

                <Button
                  type="submit"
                  className="w-full h-12 text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                  size="lg"
                  disabled={
                    isProcessing ||
                    !agreeToTerms ||
                    (checkoutValidation !== null && !checkoutValidation.isValid) ||
                    // When booking flights directly via Duffel, the submit
                    // button drives tokenize + 3DS + backend /instant in
                    // one go, so the card form must be fully valid first.
                    (canUseDuffelDirect && (!duffelClientKey || !duffelCardFlow.isFormValid))
                  }
                >
                  {isProcessing ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-5 w-5 animate-spin" />
                      Processing...
                    </span>
                  ) : canUseDuffelDirect ? (
                    <span className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      Pay ${cart.totalPrice.toFixed(2)} now
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Lock className="h-4 w-4" />
                      Proceed to Secure Payment
                    </span>
                  )}
                </Button>

                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <Shield className="h-3 w-3" />
                  <span>256-bit SSL encrypted payment</span>
                </div>
            </CardContent>
          </Card>
          </div>
        </div>
      </form>
      </div>
    </div>
  )
}
